// Central role helpers — mantém consistência com backend (server.py :: ROLE_*)
export const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin: "Admin",
  gerente: "Gerente",
  supervisor: "Supervisor",
  operador: "Operador",
  caixa: "Caixa",
  entregador: "Entregador",
};

// Lista completa de roles atribuíveis. `super_admin` é reservado — ver getAssignableRoles().
const ALL_ROLE_OPTIONS = [
  { value: "super_admin", label: "Super Admin", hint: "SaaS owner — vê e gerencia todas as empresas", superAdminOnly: true },
  { value: "admin", label: "Admin", hint: "Admin da empresa — gerencia usuários, cadastros e entregas" },
  { value: "gerente", label: "Gerente", hint: "Gestão da empresa (relatórios, cadastros, entregas) — sem usuários" },
  { value: "supervisor", label: "Supervisor", hint: "Operacional avançado — atribui e valida entregas" },
  { value: "operador", label: "Operador", hint: "Operacional básico (baseado em permissões)" },
  { value: "caixa", label: "Caixa", hint: "Caixa/financeiro (baseado em permissões)" },
  { value: "entregador", label: "Entregador", hint: "App mobile — apenas suas próprias entregas" },
];

// Retorna apenas os roles que o `actor` pode atribuir. Trava de segurança:
// somente Super Admin pode conceder o role "super_admin" a outros usuários.
// Alinha com backend em `_validate_role_assignment`.
export const getAssignableRoles = (actor) => {
  const canAssignSA = isSuperAdmin(actor);
  return ALL_ROLE_OPTIONS.filter((r) => !r.superAdminOnly || canAssignSA);
};

// Backward-compat: alguns callers ainda importam ROLE_OPTIONS estático.
// Aqui removemos super_admin para uso genérico — quem precisar da lista completa usa ALL_ROLE_OPTIONS.
export const ROLE_OPTIONS = ALL_ROLE_OPTIONS.filter((r) => !r.superAdminOnly);

// Roles com poderes administrativos (require_admin no backend)
const ADMIN_LIKE = new Set(["super_admin", "admin", "gerente"]);
const USER_MANAGERS = new Set(["super_admin", "admin"]);

// Super Admin = SaaS owner. Regra ESTRITA: role compatível E empresa_ids vazio.
// Se um usuário tem role="super_admin" mas empresa_ids=[X], NÃO é super admin
// (estado inconsistente que agora é rejeitado no backend também).
export const isSuperAdmin = (user) => {
  if (!user) return false;
  if (user._impersonating) return false;
  const roleCompatible = user.role === "super_admin" || user.role === "admin";
  const noEmpresa = !user.empresa_ids || user.empresa_ids.length === 0;
  return roleCompatible && noEmpresa;
};

export const isAdminLike = (user) => !!user && ADMIN_LIKE.has(user.role);
export const isUserManager = (user) => !!user && USER_MANAGERS.has(user.role);

// Retorna true se o user tem AO MENOS uma view=true em qualquer recurso da matrix.
// Usado pra detectar contas "vazias" (sem permissão nenhuma) antes de tentar renderizar
// rotas protegidas — evita loop de redirect quando toda rota falha (bug fev/2026).
export const hasAnyView = (user) => {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  // Modo Suporte (SA impersonando tenant): permite acesso total no frontend.
  // Backend ainda valida perms reais via cookie — isso só destrava o navigation shell.
  if (user._impersonating) return true;
  const matrix = user.perms_matrix;
  if (!matrix || typeof matrix !== "object") return false;
  return Object.values(matrix).some((r) => r?.v);
};

// Matrix é DEFINITIVA (fev/2026). Sem fallback role para não-SA.
// - super_admin bypass
// - _impersonating (Modo Suporte) → bypass (backend continua validando por token)
// - matrix ausente/null → deny total
// - matrix presente → matrix decide (empty {} = deny tudo, pois nenhum resource nela)
export const hasPerm = (user, resource, action) => {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  if (user._impersonating) return true;
  const matrix = user.perms_matrix;
  if (!matrix || typeof matrix !== "object") return false;
  return !!(matrix[resource] || {})[action];
};

export const roleBadgeClass = (role) => ({
  super_admin: "bg-purple-100 text-purple-700",
  admin: "bg-orange-100 text-orange-700",
  gerente: "bg-amber-100 text-amber-700",
  supervisor: "bg-teal-100 text-teal-700",
  operador: "bg-slate-100 text-slate-700",
  caixa: "bg-emerald-100 text-emerald-700",
  entregador: "bg-blue-100 text-blue-700",
}[role] || "bg-slate-100 text-slate-700");

// Papel efetivo (leva em conta o escopo real)
export const effectiveRole = (user) => {
  if (isSuperAdmin(user)) return "super_admin";
  return user?.role || "operador";
};
